import { useCallback } from 'react'
import { SocketService } from '@/services/socket'
import { logger } from '@/lib'
import { useNotification } from '@/hooks/useNotification'

interface UseUserSocketProps {
  onReconnect?: () => void
  onMessage?: (message: any) => void
  messageType?: string
}

export const useUserSocket = ({ onReconnect, onMessage, messageType }: UseUserSocketProps = {}) => {
  const { openNotification } = useNotification()

  const initSocketConnection = useCallback(() => {
    SocketService.connect({
      onReconnect,
      onMessage: async (event) => {
        try {
          const message = JSON.parse(event.data)
          if (message.type || message.body) {
            console.debug('Received socket message:', message, messageType)
          }

          if (messageType && message.type !== messageType) {
            return
          }

          if (message.title && message.body) {
            if ('serviceWorker' in navigator && 'PushManager' in window) {
              try {
                const serviceWorkerRegistration = await navigator.serviceWorker.ready
                await serviceWorkerRegistration.showNotification(message.title, {
                  body: message.body,
                  icon: message.icon || '/logo192.png',
                  badge: '/favicon.png',
                  data: {
                    dateOfArrival: Date.now(),
                    primaryKey: message.id || '1',
                  },
                })
              } catch (error) {
                logger.error('Error showing push notification:', error)
              }
            }
          }

          onMessage?.(message)
        } catch (error) {
          logger.error('Error processing socket message:', error)
        }
      },
    })
  }, [messageType, onReconnect, onMessage, openNotification])

  const sendMessage = (payload: Record<string, any>) => {
    SocketService.send({ type: messageType, payload })
  }

  return { initSocketConnection, sendMessage }
}
